import { IMG } from "@/data/content";

export const ARTICLES_2 = [
  {
    slug: "understanding-commercial-utility-bills",
    title: "Understanding Commercial Utility Bills",
    category: "utility-bills",
    excerpt:
      "A line-by-line guide to the commercial energy bill — energy vs. demand, supply vs. delivery, and the charges that quietly drive your total cost.",
    readingTime: "10 min",
    updated: "2026-06-09",
    added: "2026-06-01",
    image: IMG.officeTower,
    featured: true,
    popular: true,
    keywords: ["utility bill", "demand", "energy charge", "delivery", "supply", "line items"],
    intro:
      "Commercial energy bills are complex — often intentionally so. But once you understand the handful of components that make up nearly every bill, the mystery disappears. This guide walks through what's actually on your bill and which parts you can influence through procurement and load management.",
    sections: [
      {
        id: "two-halves",
        heading: "The two halves of every bill: supply and delivery",
        blocks: [
          { p: "Almost every commercial bill splits into two broad categories. Supply is the cost of the energy commodity itself — the part you can shop competitively in deregulated markets. Delivery is the regulated cost of getting that energy to your facility over the utility's wires and pipes." },
          { note: "Procurement affects the supply side. Delivery is set by regulators and billed by your utility no matter which supplier you choose." },
        ],
      },
      {
        id: "energy-vs-demand",
        heading: "Energy vs. demand: the crucial distinction",
        blocks: [
          { p: "For electricity, the supply and delivery costs both break down further into energy and demand components." },
          { ul: [
            "Energy (kWh): the total volume of electricity you consumed — like the total miles driven.",
            "Demand (kW): your highest sustained rate of consumption — like your top speed.",
          ] },
          { p: "Two facilities can consume identical total energy yet pay very different bills if one has sharp demand peaks. This is why demand charges deserve close attention." },
        ],
      },
      {
        id: "common-line-items",
        heading: "Common line items explained",
        blocks: [
          { ul: [
            "Energy/commodity charge: the price for kWh or therms consumed.",
            "Demand charge: based on your peak kW during the period.",
            "Capacity charge: your share of generation kept on standby for peak reliability.",
            "Transmission charge: cost of the high-voltage regional network.",
            "Distribution/delivery charge: cost of the local wires and pipes.",
            "Taxes, riders, and fees: regulated add-ons that vary by utility and state.",
          ] },
        ],
      },
      {
        id: "gas-bills",
        heading: "How natural gas bills differ",
        blocks: [
          { p: "Gas bills follow the same supply-vs-delivery logic. The commodity (measured in therms or dekatherms) is competitive in many markets, while the local distribution company charges regulated delivery. Gas bills are strongly seasonal, climbing in winter with heating demand." },
        ],
      },
      {
        id: "reading-interval",
        heading: "Reading your usage (interval) data",
        blocks: [
          { p: "Most commercial meters record usage in 15-minute or hourly intervals. This interval data reveals your load shape — when and how intensely you use energy — and is the raw material for both accurate quotes and demand management." },
        ],
      },
      {
        id: "what-you-control",
        heading: "What you can actually control",
        blocks: [
          { p: "You can shop the commodity, choose a contract structure, and manage demand and peak usage. You generally cannot change regulated delivery, transmission, and distribution rates — though when you use power can influence peak-based transmission and capacity costs." },
        ],
      },
      {
        id: "mistakes",
        heading: "Common mistakes",
        blocks: [
          { ul: [
            "Focusing only on the commodity rate while ignoring demand and capacity charges.",
            "Not reviewing interval data to find peak-reduction opportunities.",
            "Assuming the utility default supply rate is competitive.",
            "Overlooking riders and fees that add up across many meters.",
          ] },
        ],
      },
    ],
    faqs: [
      { q: "Why is my demand charge so high even though I didn't use more energy?", a: "Demand charges are based on your peak rate of use, not total consumption. A short spike — like starting large equipment at once — can set a high demand charge for the whole billing period." },
      { q: "Which parts of my bill can procurement lower?", a: "Procurement targets the supply (commodity) portion. Delivery, transmission, and distribution are regulated, though peak management can influence some peak-based charges." },
      { q: "What is a rider on my bill?", a: "A rider is a regulated surcharge or adjustment added by the utility or state to recover specific costs. Riders vary by utility and can change over time." },
    ],
    related: {
      articles: ["understanding-demand-charges", "understanding-capacity-charges", "understanding-load-factor", "utility-vs-energy-supplier"],
      solutions: ["commercial-electricity", "commercial-natural-gas"],
      industries: ["commercial-real-estate", "manufacturing"],
      states: ["tx", "il", "pa", "ny"],
    },
  },
  {
    slug: "understanding-demand-charges",
    title: "Understanding Demand Charges",
    category: "utility-bills",
    excerpt:
      "Demand charges can quietly dominate a commercial electricity bill. Learn what drives them, how they're calculated, and how to manage them.",
    readingTime: "8 min",
    updated: "2026-06-06",
    added: "2026-06-02",
    image: IMG.manufacturing,
    popular: true,
    keywords: ["demand charge", "peak demand", "kW", "peak shaving", "load management"],
    intro:
      "For many commercial accounts, demand charges are among the largest and least understood parts of the bill. Because they're based on peak usage rather than total consumption, they behave differently from the energy charge — and offer real opportunities to save.",
    sections: [
      {
        id: "what",
        heading: "What a demand charge is",
        blocks: [
          { p: "A demand charge bills you for your highest sustained rate of electricity use (in kilowatts) during the billing period. It reflects the capacity that the grid and utility infrastructure must be able to deliver to you at any moment — even if you only hit that level briefly." },
        ],
      },
      {
        id: "how-measured",
        heading: "How demand is measured",
        blocks: [
          { p: "Utilities record demand in short intervals, commonly 15 minutes. Your billed demand is usually the highest single interval in the period. Some tariffs use a 'ratchet,' where a high peak can influence charges for months afterward." },
          { note: "A demand ratchet means one bad peak can echo across future bills — making peak management especially valuable." },
        ],
      },
      {
        id: "what-drives",
        heading: "What drives your peak",
        blocks: [
          { p: "Peaks are often caused by many loads running simultaneously — for example, starting multiple pieces of large equipment at the same time, or HVAC ramping up on a hot afternoon while production is at full tilt." },
        ],
      },
      {
        id: "manage",
        heading: "How to manage demand charges",
        blocks: [
          { ul: [
            "Stagger equipment startup so large loads don't spike at once.",
            "Shift flexible loads to off-peak times.",
            "Use controls/automation to cap simultaneous demand.",
            "Deploy on-site battery storage to shave peaks.",
            "Review interval data to identify and target your worst peaks.",
          ] },
        ],
      },
      {
        id: "mistakes",
        heading: "Common mistakes",
        blocks: [
          { ul: [
            "Assuming a lower commodity rate will offset high demand charges — it often won't.",
            "Ignoring interval data that pinpoints peak events.",
            "Overlooking demand ratchets in the tariff.",
          ] },
        ],
      },
    ],
    faqs: [
      { q: "Can I reduce demand charges without reducing production?", a: "Often yes. Staggering equipment startup, shifting flexible loads, and using controls or storage can lower peaks without cutting output." },
      { q: "What is a demand ratchet?", a: "A tariff provision where a high peak sets a floor for demand billing over subsequent months, so a single spike can raise charges well beyond the month it occurred." },
    ],
    related: {
      articles: ["understanding-commercial-utility-bills", "understanding-load-factor", "understanding-capacity-charges", "commercial-electricity-buying-strategies"],
      solutions: ["commercial-electricity"],
      industries: ["manufacturing", "warehousing-logistics", "data-centers"],
      states: ["tx", "il", "pa"],
    },
  },
  {
    slug: "understanding-capacity-charges",
    title: "Understanding Capacity Charges",
    category: "utility-bills",
    excerpt:
      "Capacity charges pay for generation kept on standby to meet peak demand — and they're often set by your usage during just a few peak hours a year.",
    readingTime: "8 min",
    updated: "2026-06-05",
    added: "2026-06-03",
    image: IMG.transmissionNight,
    keywords: ["capacity", "capacity charge", "peak hours", "capacity tag", "pjm", "icap"],
    intro:
      "Capacity charges are one of the more opaque components of a commercial electricity bill, yet they can represent a significant share of your all-in cost. Understanding how they're set reveals a powerful, often-overlooked opportunity to save.",
    sections: [
      {
        id: "what",
        heading: "What capacity charges pay for",
        blocks: [
          { p: "To keep the lights on during the highest-demand moments of the year, grid operators must ensure enough generation is available — plus a reserve margin — even if some of it runs only a few hours annually. Capacity charges recover the cost of maintaining that standby capability." },
        ],
      },
      {
        id: "how-set",
        heading: "How your capacity charge is set",
        blocks: [
          { p: "In many markets, your capacity obligation is based on your usage during a small number of system peak hours in the prior year — sometimes called your capacity tag or Installed Capacity (ICAP) tag. That tag can then apply for the following delivery year." },
          { note: "Because a few peak hours can define your capacity cost for an entire year, reducing usage during those specific windows is uniquely valuable." },
        ],
      },
      {
        id: "peak-management",
        heading: "Managing capacity costs",
        blocks: [
          { p: "The strategy is to reduce load during the hours most likely to set the system peak — typically the hottest afternoons of summer in many regions. Predicting and curtailing during these windows (peak shaving) can lower your capacity tag for the following year." },
          { ul: [
            "Monitor peak-day forecasts and alerts.",
            "Curtail non-essential load during likely peak intervals.",
            "Use on-site storage or generation to reduce grid draw at peaks.",
          ] },
        ],
      },
      {
        id: "mistakes",
        heading: "Common mistakes",
        blocks: [
          { ul: [
            "Not realizing capacity is driven by a handful of hours, not total usage.",
            "Ignoring peak-day alerts that signal curtailment opportunities.",
            "Focusing only on the commodity rate and missing capacity savings.",
          ] },
        ],
      },
    ],
    faqs: [
      { q: "Are capacity charges the same everywhere?", a: "No. The mechanics vary by grid operator and market. In some regions capacity is a distinct, sizable component; in others it's embedded differently. The peak-hour principle, however, is common." },
      { q: "How can I lower my capacity tag?", a: "By reducing your usage during the specific peak hours that set the tag. This requires monitoring peak forecasts and being able to curtail or offset load at those times." },
    ],
    related: {
      articles: ["understanding-demand-charges", "understanding-commercial-energy-markets", "understanding-commercial-utility-bills"],
      solutions: ["commercial-electricity"],
      industries: ["manufacturing", "data-centers"],
      states: ["pa", "nj", "md", "il"],
    },
  },
  {
    slug: "understanding-load-factor",
    title: "Understanding Load Factor",
    category: "utility-bills",
    excerpt:
      "Load factor measures how steadily you use electricity relative to your peak — and it directly affects the pricing suppliers offer you.",
    readingTime: "7 min",
    updated: "2026-06-04",
    added: "2026-06-04",
    image: IMG.dataCenter,
    keywords: ["load factor", "peak demand", "average demand", "utilization", "efficiency"],
    intro:
      "Load factor is a simple ratio with a big impact. It tells you — and your suppliers — how efficiently you use the capacity you draw. A better load factor can mean better pricing and lower demand-related costs.",
    sections: [
      {
        id: "what",
        heading: "What load factor is",
        blocks: [
          { p: "Load factor is the ratio of your average demand to your peak demand over a period, expressed as a percentage. A high load factor means your usage is steady and predictable; a low load factor means you have sharp peaks relative to your average consumption." },
          { note: "Simplified: Load Factor = (total kWh used) ÷ (peak kW × hours in the period). Higher is generally better." },
        ],
      },
      {
        id: "why-matters",
        heading: "Why it matters",
        blocks: [
          { p: "Suppliers and utilities prefer steady, high-load-factor accounts because they're cheaper and less risky to serve — the infrastructure serving your peak is used more of the time. That efficiency often translates into more favorable pricing and lower demand charges relative to your energy use." },
        ],
      },
      {
        id: "improve",
        heading: "How to improve load factor",
        blocks: [
          { ul: [
            "Flatten peaks by staggering equipment and managing HVAC.",
            "Shift flexible loads into otherwise low-usage hours.",
            "Use storage to smooth the profile the grid sees.",
          ] },
          { p: "Improving load factor is really about reducing peaks and/or filling in valleys so your consumption is more even over time." },
        ],
      },
      {
        id: "example",
        heading: "A quick example",
        blocks: [
          { p: "A facility that runs steadily around the clock will have a high load factor and be attractive to serve. A facility that sits idle most of the day but spikes hard for a few hours has a low load factor — and will likely see higher demand charges and less favorable pricing, even at similar total consumption." },
        ],
      },
    ],
    faqs: [
      { q: "What's a 'good' load factor?", a: "It varies by facility type, but higher is generally better. Continuous operations naturally run high; businesses with short, intense usage periods run lower and have more room to improve." },
      { q: "Does improving load factor lower my bill?", a: "It can — by reducing peak-driven demand charges and potentially earning more favorable supply pricing, since steady load is cheaper to serve." },
    ],
    related: {
      articles: ["understanding-demand-charges", "understanding-commercial-utility-bills", "commercial-electricity-buying-strategies"],
      solutions: ["commercial-electricity"],
      industries: ["data-centers", "manufacturing", "warehousing-logistics"],
      states: ["tx", "il"],
    },
  },
];
